import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Home, Calendar, Wrench, Building, Users, Building2, CheckCircle, 
  TrendingUp, Shield, Clock, KeyRound, MapPin, Handshake, Phone,
  ClipboardCheck, Settings, Paintbrush, ThumbsUp, Award
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: ClipboardCheck,
      title: 'Whole-Home Assessment',
      description: 'A thorough walkthrough that documents your home\'s condition and creates your personalized Home Profile.',
      featured: true
    },
    {
      icon: Wrench,
      title: 'Repairs & Maintenance',
      description: 'From minor fixes to major repairs — handled by trusted professionals who know your home.',
      featured: false
    },
    {
      icon: Settings,
      title: 'Preventive Care',
      description: 'Scheduled maintenance to catch small issues before they become expensive problems.',
      featured: false
    },
    {
      icon: Calendar,
      title: 'Seasonal Services',
      description: 'Gutter cleaning, HVAC tune-ups, winterization, and more — timed for when your home needs it.',
      featured: false
    },
    {
      icon: Paintbrush,
      title: 'Home Improvements',
      description: 'Updates and upgrades planned around your budget and timeline.',
      featured: false
    },
    {
      icon: Building,
      title: 'Property Management Support',
      description: 'Multi-property assessments and coordinated care for landlords and investors.',
      featured: false
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Waitlist Banner */}
      <div className="bg-[#CFB973] py-4 mt-[120px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 text-center">
            <Clock className="w-6 h-6 text-[#003366]" />
            <p className="text-[#003366] font-semibold">
              <span className="block sm:inline">Currently at capacity.</span>{' '}
              <span className="block sm:inline">Join our waitlist — scheduling opens soon.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section - Premium Style */}
      <section className="relative py-20 md:py-24 bg-[#003366] text-white overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              Comprehensive Home
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Care Services</span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Designed for peace of mind — all starting with <span className="text-[#CFB973] font-semibold">understanding your home</span>.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-7 text-lg shadow-xl">
                Join the Waitlist
              </Button>
            </Link>
            <Link to="/remodeling">
              <Button size="lg" className="bg-transparent hover:bg-white/10 text-white font-semibold px-10 py-7 text-lg border-2 border-[#CFB973]">
                Remodeling (Open Now)
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-[#E8EEF3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">Licensed & Insured</p>
              <p className="text-[#003366]/60 text-xs">#13VH13590600</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <ThumbsUp className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">100% Satisfaction</p>
              <p className="text-[#003366]/60 text-xs">Guarantee</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Award className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">10+ Years</p>
              <p className="text-[#003366]/60 text-xs">Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">What We Offer</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Every service is informed by your Home Profile — so recommendations are always personalized to your home's actual needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={idx} 
                  className={`border-2 transition-all duration-300 hover:shadow-xl ${
                    service.featured 
                      ? 'border-[#CFB973] bg-gradient-to-b from-[#CFB973]/10 to-white' 
                      : 'border-gray-100 hover:border-[#CFB973]'
                  }`}
                >
                  <CardContent className="p-8 text-center">
                    {service.featured && (
                      <span className="inline-block bg-[#CFB973] text-[#003366] text-xs font-bold px-3 py-1 rounded-full mb-4">
                        START HERE
                      </span>
                    )}
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ${
                      service.featured ? 'bg-[#CFB973]' : 'bg-[#003366]'
                    }`}>
                      <Icon className={`w-10 h-10 ${service.featured ? 'text-[#003366]' : 'text-white'}`} />
                    </div>
                    <h3 className="text-xl font-bold text-[#003366] mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button size="lg" className="bg-[#003366] hover:bg-[#003366]/90 text-white font-semibold px-10 py-6">
                Join the Waitlist
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 bg-[#E8EEF3]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">Who We Serve</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whether you've lived in your home for decades or just got the keys yesterday — we're here to help.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Homeowners */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-[#CFB973]">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-6 shadow-lg">
                <Home className="w-8 h-8 text-[#003366]" />
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-3">Homeowners</h3>
              <p className="text-gray-600 text-sm mb-4">
                You take pride in your home. We help you protect it with a clear plan and trusted support.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Proactive maintenance</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>No surprise emergencies</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>One trusted partner</span>
                </li>
              </ul>
            </div>

            {/* New Homebuyers */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-[#CFB973]">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-6 shadow-lg">
                <KeyRound className="w-8 h-8 text-[#003366]" />
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-3">New Homebuyers</h3>
              <p className="text-gray-600 text-sm mb-4">
                Just closed? Start with clarity. Our assessment tells you what's good, what to watch, and what to plan for.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Beyond the inspection</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Prioritized action list</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Budget planning help</span>
                </li>
              </ul>
            </div>

            {/* Out of State Movers */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-[#CFB973]">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-6 shadow-lg">
                <MapPin className="w-8 h-8 text-[#003366]" />
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-3">New to the Area?</h3>
              <p className="text-gray-600 text-sm mb-4">
                Moved from out of state? Skip the guesswork. We're your single point of contact for everything home-related.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>No contractor hunting</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Local expertise</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Vetted professionals</span>
                </li>
              </ul>
            </div>

            {/* Realtors */}
            <div className="bg-[#003366] rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-[#CFB973]">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-6 shadow-lg">
                <Handshake className="w-8 h-8 text-[#003366]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Realtors & Agents</h3>
              <p className="text-white/80 text-sm mb-4">
                Give your buyers a trusted resource after closing. We make you look good and keep clients happy long-term.
              </p>
              <ul className="text-sm text-white/70 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Post-closing referrals</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Client care packages</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                  <span>Partnership opportunities</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button size="lg" className="bg-[#003366] hover:bg-[#003366]/90 text-white font-semibold px-10 py-6">
                Let's Talk About Your Situation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Investment Properties Section */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-[#CFB973] rounded-full flex items-center justify-center shadow-lg">
                  <Building2 className="w-8 h-8 text-[#003366]" />
                </div>
                <span className="text-[#CFB973] font-bold uppercase tracking-wider text-sm">For Landlords & Investors</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Investment Property Services
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Own rental properties? A Home Profile for each property means fewer emergency calls, better tenant retention, and smarter capital planning.
              </p>
              
              <div className="space-y-6 mb-10">
                {[
                  { title: 'Multi-Property Assessments', desc: 'Discounted rates for 2+ properties. One partner for your entire portfolio.' },
                  { title: 'Tenant Turnover Prep', desc: 'Get units rent-ready fast with coordinated repairs and updates.' },
                  { title: 'Proactive Maintenance', desc: 'Prevent costly emergencies with scheduled care and early detection.' },
                  { title: 'Capital Planning Support', desc: 'Know what\'s coming. Budget for roof, HVAC, and major systems before they fail.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-[#003366]" />
                    </div>
                    <div>
                      <p className="font-bold text-white">{item.title}</p>
                      <p className="text-white/70 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/contact">
                <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-6 shadow-xl">
                  Discuss Your Portfolio
                </Button>
              </Link>
            </div>

            {/* Right - Benefits Grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: TrendingUp, title: 'Protect ROI', desc: 'Maintain property value with documented care history' },
                { icon: Shield, title: 'Reduce Liability', desc: 'Stay ahead of safety issues before they become problems' },
                { icon: Clock, title: 'Save Time', desc: 'One call handles it all — no chasing contractors' },
                { icon: Users, title: 'Happy Tenants', desc: 'Quick response and quality work means better retention' }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
                    <div className="w-14 h-14 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Icon className="w-7 h-7 text-[#003366]" />
                    </div>
                    <h3 className="font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/70 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Home Profile Tagline */}
      <section className="py-12 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl md:text-2xl text-[#003366] font-medium italic">
            "Every service starts with understanding. Your Home Profile informs every recommendation we make."
          </p>
        </div>
      </section>

      {/* Our Network Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#003366] mb-4">How We Deliver</h2>
            <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We coordinate a trusted network of licensed, insured specialists — plumbers, electricians, HVAC technicians, roofers, carpenters, and painters. Every contractor in our network is vetted for quality and reliability.
            </p>
          </div>
          
          <div className="bg-[#003366] rounded-2xl p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-[#003366]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Vetted Specialists</h3>
                <p className="text-white/70 text-sm leading-relaxed">Licensed, insured professionals we trust with our own homes.</p>
              </div>
              <div>
                <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4">
                  <Handshake className="w-8 h-8 text-[#003366]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">One Point of Contact</h3>
                <p className="text-white/70 text-sm leading-relaxed">You deal with us. We coordinate the specialists.</p>
              </div>
              <div>
                <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-[#003366]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Accountability Stays Here</h3>
                <p className="text-white/70 text-sm leading-relaxed">We stand behind every recommendation and job.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">
            Let's Discuss Your Home's Needs
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Whether you need a comprehensive assessment, ongoing maintenance, or a specific repair — we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-12 py-7 text-xl shadow-xl">
                Request a Consultation
              </Button>
            </Link>
          </div>
          
          {/* Contact Info */}
          <div className="mt-10 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-center items-center gap-6 text-gray-500">
            <a href="tel:5513799774" className="flex items-center gap-2 hover:text-[#003366] transition-colors">
              <Phone size={18} />
              <span>(551) 379-9774</span>
            </a>
            <span className="hidden sm:block">•</span>
            <span>contact@valenrockhome.services</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
